import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { useContext, useEffect, useState } from 'react';
import { FormContainer, useForm } from 'react-hook-form-mui';
import { useLocation, useNavigate } from 'react-router-dom';
import { APIContext, DialogAction } from '../../Contexts/APIContext';
import ValidatedSelectElement from '../../Utilities/Forms/ValidatedSelectElement';
import ValidatedTextField from '../../Utilities/Forms/ValidatedTextField';
import DropDownViewModel from '../../Utilities/ViewModels/DropDownViewModel';
import UpdateAdministratorSiteViewModel from '../Site/ViewModels/UpdateAdministratorSiteViewModel';
import OrgUserManagmentTable from './OrganisationUserManagment';
import SiteTable from "./SiteTable";
import CreateEditOrganisationViewModel from './ViewModels/CreateEditOrganisationViewModel';
import CircularProgress from '@mui/material/CircularProgress';
import Line from '../../Utilities/Line';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { DateField } from '@mui/x-date-pickers/DateField';
import CreateEditSiteDialog from './CreateEditSiteDialog';
import SiteTableViewModel from "./ViewModels/SiteTableViewModel";
import AuditHistoryDialog from './AuditHistoryDialog';
import { OrganisationsContext } from '../../Contexts/OrganisationsContext';

export default function EditOrganisation() {
    const location = useLocation();
    const navigate = useNavigate();
    const params = new URLSearchParams(location.search);
    const orgid = params.get('organisationid') || '';
    const { updateNextOrganisation, nextOrganisationResult } = useContext(OrganisationsContext);
    const [sitesTable, setSitesTable] = useState<SiteTableViewModel | null>(null);
    const [estimateNoticeDropdown, setEstimateNoticeDropdown] = useState<DropDownViewModel[]>([]);
    const [administratorSiteDropdown, setAdministratorSiteDropdown] = useState<DropDownViewModel[]>([]);
    const [invoiceNoticeDropdown, setInvoiceNoticeDropdown] = useState<DropDownViewModel[]>([]);
    const [invoicePayMethodDropdown, setInvoicePayMethodDropdown] = useState<DropDownViewModel[]>([]);
    const [invoicePayPeriodDropdown, setInvoicePayPeriodDropdown] = useState<DropDownViewModel[]>([]);
    const [licenseTypeDropdown, setLicenseTypeDropdown] = useState<DropDownViewModel[]>([]);
    const [locationDropdown, setLocationDropdown] = useState<DropDownViewModel[]>([]);
    const [organisation, setOrganisation] = useState<CreateEditOrganisationViewModel | null>(null);
    const [editing, setEditing] = useState<boolean>(false);
    const { post, get, openDialog, showMessage } = useContext(APIContext);
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [auditOpen, setAuditOpen] = useState<boolean>(false);
    const [tempExtensionDialogOpen, setTempExtensionDialogOpen] = useState(false);
    const [cancelTempExtensionDialogOpen, setCancelTempExtensionDialogOpen] = useState(false);
    const [tempExtensionDate, setTempExtensionDate] = useState<Dayjs | null>(null);
    const [siteCount, setSiteCount] = useState<number>(0);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    useEffect(() => {
        getOrganisation();
        getDropdowns();
    }, [orgid]);

    useEffect(() => {
        if (organisation != null) {
            getSites();
            updateNextOrganisation(organisation.id);
        }
    }, [organisation]);

    const formContext = useForm<CreateEditOrganisationViewModel>({
        defaultValues: organisation || new CreateEditOrganisationViewModel(),
    });
    const { setValue, getValues, reset, watch, formState: { isDirty, isSubmitting } } = formContext;

    useEffect(() => {
        if (organisation) {
            reset(organisation);
        }
    }, [organisation, reset]);

    useEffect(() => {
        if (organisation) {
            getSiteTable();
        }
    }, [organisation]);

    const refreshData = async () => {
        await getOrganisation();
    };

    const getDropdowns = async () => {
        get('Organisation', 'GetEstimateNoticeDropdown', setEstimateNoticeDropdown);
        get('Organisation', 'GetInvoiceNoticeDropdown', setInvoiceNoticeDropdown);
        get('Organisation', 'GetInvoicePayMethodDropdown', setInvoicePayMethodDropdown);
        get('Organisation', 'GetInvoicePayPeriodDropdown', setInvoicePayPeriodDropdown);
        get('Organisation', 'GetLicenseTypeDropdown', setLicenseTypeDropdown);
        get('Organisation', 'GetLocationDropdown', setLocationDropdown);
    };
    const getSites = async () => {
        get('OrganisationSite', `GetSitesForOrganisationDropdown?organisationId=${organisation?.id}`, setAdministratorSiteDropdown);
        setSiteCount(administratorSiteDropdown.length)
    }

    const getOrganisation = async () => {
        await get('Organisation', `GetOrganisationById?Id=${orgid}`, setOrganisation);
    };

    const getSiteTable = async () => {
        await get('OrganisationSite', `GetSiteTable?organisationId=${organisation?.id}`, setSitesTable);
    }

    const updateOrganisation = async (form: CreateEditOrganisationViewModel) => {
        if (organisation == null) { return }

        const adminSiteDetails = new UpdateAdministratorSiteViewModel();
        adminSiteDetails.siteId = form.administratorSiteId;
        adminSiteDetails.organisationId = organisation.id;
        await post('OrganisationSite', 'UpdateAdministratorSiteForOrganisation', adminSiteDetails);

        await post('Organisation', 'UpdateOrganisation', form);
        refreshData();
        setEditing(false);
    };

    const closeDialog = (refresh: boolean) => {
        setDialogOpen(false);
        if (refresh) {
            refreshData();
        }
    };

    const handleDateChange = (
        date: Dayjs | null,
        field: 'renewalDate' | 'expiryDate' | 'tempExpiryDate'
    ) => {
        setValue(field, date ? date.toDate() : null, {
            shouldDirty: true,
            shouldValidate: true
        });
    };

    const addSite = () => {
        setDialogOpen(true);
    };

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const saveTempExtension = async (date: Date | null, onSuccess: () => void) => {
        if (organisation == null) return;

        const form = getValues();
        const payload = {
            ...form,
            id: organisation.id,
            tempExpiryDate: date
        };

        await post('Organisation', 'UpdateOrganisation', payload, () => {
            showMessage('success', date ? 'Temporary extension saved' : 'Temporary extension cancelled');
            refreshData();
            setEditing(false);
            onSuccess();
        });
    };

    const openSetTempExtensionDialog = () => {
        setTempExtensionDate(dayjs(expiryDate) ?? dayjs());
        setTempExtensionDialogOpen(true);
        handleClose();
    };

    const confirmSetTempExtension = async () => {
        await saveTempExtension(tempExtensionDate ? tempExtensionDate.toDate() : null, () => {
            setTempExtensionDialogOpen(false);
        });
    };

    const confirmCancelTempExtension = () => {
        setCancelTempExtensionDialogOpen(true);
        handleClose();
    };

    const handleTempExtensionAction = () => {
        if (organisation?.tempExpiryDate) {
            confirmCancelTempExtension();
            return;
        }
        openSetTempExtensionDialog();
    };

    const renewLicense = async () => {
        await post('organisation', 'RenewLicense', organisation);
        refreshData();
    };
    const startEditing = async () => {
        setEditing(true);
        handleClose();
    };
    const stopEditing = async () => {
        setEditing(false);
        refreshData();
    };

    const archive = async () => {
        openDialog(
            <>Are you sure you want to archive {organisation!.organisationName}?</>,
            [new DialogAction("Archive", () => {
                get('organisation', `ArchiveOrganisation?Id=${organisation!.id}`, () => {
                    showMessage('success', 'Organisation archived');
                    navigate('/admin/manageorganisations');
                });
            })],
            "Archive Organisation",
            "Cancel"
        );
    };

    const restore = async () => {
        openDialog(
            <>Are you sure you want to restore {organisation!.organisationName}?</>,
            [new DialogAction("Restore", async () => {
                await get('organisation', `RestoreOrganisation?Id=${organisation!.id}`, () => {
                    showMessage('success', 'Organisation restored');
                    refreshData();
                });
            })],
            "Restore Organisation",
            "Cancel"
        );
    };

    function getAdjustedMonthName(date: Dayjs, dropdownList: DropDownViewModel[], id: string): string {
        if (dropdownList.length > 0 && organisation != null) {
            const selected = dropdownList.find(f => f.id == id)?.label.charAt(0);
            if (selected != null) {
                const num = -parseInt(selected);
                return date.add(num, 'month').format('MMMM');
            }
        }
        return "";
    }

    const goToNextOrg = () => {
        if (nextOrganisationResult)
            navigate(`/admin/manageorganisation?organisationid=${nextOrganisationResult}`)
    }

    const licenseTypeId = watch("licenseTypeId");
    const corporateLicense = licenseTypeDropdown.find(x => x.label === 'Multi-Office');
    const isCorporate = (licenseTypeId === corporateLicense?.id);
    const estimateNoticeId = watch("estimateNoticeId");
    const invoiceNoticeId = watch("invoiceNoticeId");
    const invalidNotices = (invoiceNoticeDropdown.find(f => f.id == invoiceNoticeId)?.label?.charAt(0) ?? 0) > (estimateNoticeDropdown.find(f => f.id == estimateNoticeId)?.label?.charAt(0) ?? 0);

    const expiryDate = watch("expiryDate");
    const renewalDate = watch("renewalDate");
    const tempExpiryDate = watch("tempExpiryDate");
    const hasTempExtension = organisation?.tempExpiryDate != null;

    return (
        organisation !== null ?
            <Grid container>
                <Grid>
                    <Typography variant="h4" gutterBottom>
                        Manage Organisation - {organisation.organisationName}
                    </Typography>
                </Grid>
                <Grid container>
                    <Grid>
                        <Button onClick={() => navigate("/admin/manageorganisations")}>Back to Results</Button>
                    </Grid>
                    <Grid>
                        <Button disabled={nextOrganisationResult === null || nextOrganisationResult === organisation.id} onClick={goToNextOrg}>Next Organisation</Button>
                    </Grid>
                </Grid>
                <Grid size={12} component={Paper}>
                    <FormContainer formContext={formContext} onSuccess={updateOrganisation}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'en-gb'}>
                            <Grid container alignItems="flex-start">
                                <Grid>
                                    <Typography variant="h6">Organisation Details{editing && " - Editing"}</Typography>
                                </Grid>
                                {organisation.archived && (
                                    <Grid size={12}>
                                        <Alert severity="warning">This organisation is archived. Restore it to make changes.</Alert>
                                    </Grid>
                                )}
                                <Grid>
                                    <Button endIcon={<KeyboardArrowDownIcon />} onClick={handleClick}>Licence Actions</Button>
                                    <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                                        {organisation.archived ? (
                                            <MenuItem onClick={restore}>Restore Organisation</MenuItem>
                                        ) : (
                                            <>
                                                <MenuItem onClick={startEditing} disabled={editing}>Edit Organisation Details</MenuItem>
                                                <MenuItem onClick={handleTempExtensionAction}>
                                                    {hasTempExtension ? "Cancel Temporary Extension" : "Temporarily Extend Licence"}
                                                </MenuItem>
                                                <MenuItem onClick={() => setAuditOpen(true)}>View Audit History</MenuItem>
                                                <MenuItem onClick={archive}>Archive Organisation</MenuItem>
                                            </>
                                        )}
                                    </Menu>
                                </Grid>
                                <Line />
                                <Grid size={6} container>
                                    <Grid size={4}>
                                        <ValidatedTextField
                                            label="Organisation Name"
                                            name="organisationName"
                                            readOnly={!editing}
                                            required
                                        />

                                    </Grid>
                                    <Grid size={4}>
                                        <TextField label="Member Number" value={organisation.memberNumber} />
                                    </Grid>
                                    <Grid size={4}>
                                        <ValidatedSelectElement
                                            label="License Type"
                                            name="licenseTypeId"
                                            options={licenseTypeDropdown}
                                            readOnly={!editing}
                                            required
                                        />
                                    </Grid>

                                    <Grid size={4}>
                                        <ValidatedSelectElement
                                            label="Location"
                                            name="locationId"
                                            options={locationDropdown}
                                            readOnly={!editing}
                                            required
                                        />
                                    </Grid>
                                    <Grid size={8}>
                                        <ValidatedSelectElement
                                            label="Primary Office Location"
                                            name="administratorSiteId"
                                            options={administratorSiteDropdown}
                                            value={organisation?.administratorSiteId}
                                            readOnly={!editing}
                                        />
                                    </Grid>
                                    <Grid size={2}>
                                        <DateField
                                            label="Renewal Date"
                                            value={dayjs(renewalDate) ?? null}
                                            format="DD/MM"
                                            onChange={(date) => handleDateChange(date, "renewalDate")}
                                            readOnly={!editing}
                                            slotProps={{ textField: { size: 'small' } }}
                                        />
                                    </Grid>
                                    <Grid size={3}>
                                        <DatePicker
                                            label="Expiry Date"
                                            value={dayjs(expiryDate) ?? null}
                                            onChange={(date) => handleDateChange(date, "expiryDate")}
                                            format="DD/MM/YYYY"
                                            slotProps={{ textField: { size: 'small' } }}
                                            readOnly={!editing}
                                        />
                                    </Grid>
                                    <Grid size={2}>
                                        {!organisation.archived && <Button
                                            variant="contained"
                                            size="small"
                                            onClick={renewLicense}
                                            disabled={isSubmitting || organisation == null}
                                        >
                                            +1 Year
                                        </Button>}
                                    </Grid>
                                    <Grid size={3}>
                                        {hasTempExtension && (
                                            <DatePicker
                                                label="Temp Expiry Date"
                                                value={dayjs(tempExpiryDate) ?? null}
                                                onChange={(date) => handleDateChange(date, "tempExpiryDate")}
                                                format="DD/MM/YYYY"
                                                minDate={dayjs(expiryDate) ?? dayjs()}
                                                slotProps={{ textField: { size: 'small' } }}
                                                readOnly={!editing}
                                            />
                                        )}
                                    </Grid>
                                    <Grid size={2}>
                                        {!organisation.archived && <Button
                                            variant="contained"
                                            size="small"
                                            onClick={handleTempExtensionAction}
                                            disabled={isSubmitting || organisation == null}
                                        >
                                            {hasTempExtension ? "Cancel Ext" : "+ Temp Ext"}
                                        </Button>}
                                    </Grid>

                                    <Grid size={4}>
                                        <TextField label="Site Count" value={organisation.sites.length} />
                                    </Grid>
                                    <Grid size={4}>
                                        <TextField label="Active Users" value={organisation.totalMembers} />
                                    </Grid>
                                    <Grid size={4}>
                                        <TextField label="Organisation Managers" value={organisation.totalManagers} />
                                    </Grid>
                                </Grid>
                                {licenseTypeId !== "17b6994c-9d71-4cd1-bb79-e29d36ae36e2" &&
                                    <Grid size={6} container alignItems="flex-start">
                                        <Grid size={12}>
                                            <ValidatedTextField
                                                label="Invoice Payee"
                                                name="invoicePayee"
                                                readOnly={!editing}
                                                required
                                            />
                                        </Grid>

                                        <Grid size={4}>
                                            <ValidatedSelectElement
                                                label="Invoice Payment Period"
                                                name="invoicePayPeriodId"
                                                options={invoicePayPeriodDropdown}
                                                readOnly={!editing}
                                                required
                                            />
                                        </Grid>

                                        <Grid size={8}>
                                            <ValidatedSelectElement
                                                label="Invoice Payment Method"
                                                name="invoicePayMethodId"
                                                options={invoicePayMethodDropdown}
                                                readOnly={!editing}
                                                required
                                            />
                                        </Grid>

                                        <Grid size={4}>
                                            <ValidatedSelectElement
                                                label="Invoice Notice"
                                                name="invoiceNoticeId"
                                                options={invoiceNoticeDropdown}
                                                readOnly={!editing}
                                                required
                                            />
                                        </Grid>
                                        <Grid size={8}>
                                            <TextField label="Invoice Notice Month" value={getAdjustedMonthName(dayjs(organisation.expiryDate), invoiceNoticeDropdown, invoiceNoticeId)} />
                                        </Grid>
                                        <Grid size={4}>
                                            <ValidatedSelectElement
                                                label="Estimate Notice"
                                                name="estimateNoticeId"
                                                options={estimateNoticeDropdown}
                                                readOnly={!editing}
                                                required
                                            />
                                        </Grid>
                                        <Grid size={8}>
                                            <TextField label="Invoice Estimate Month" value={getAdjustedMonthName(dayjs(organisation.expiryDate), estimateNoticeDropdown, estimateNoticeId)} />
                                        </Grid>
                                        {invalidNotices &&
                                            <Grid size={12}>
                                                <Alert severity="error">Estimate Notice should be before Invoice Notice</Alert>
                                            </Grid>
                                        }
                                    </Grid>
                                }
                                {editing && (
                                    <>
                                        <Grid size={3}>
                                            <Button onClick={stopEditing}>Cancel</Button>
                                        </Grid>
                                        <Grid size={3}>
                                            <Button
                                                type="submit"
                                                disabled={invalidNotices || !isDirty || isSubmitting || !isCorporate && (organisation?.sites.length ?? 0) > 1}>
                                                {isSubmitting ? 'Saving...' : 'Update Organisation'}
                                            </Button>
                                        </Grid>
                                    </>
                                )}
                                {!isCorporate && (organisation?.sites.length ?? 0) > 1 && (
                                    <Grid size={12}>
                                        <Typography variant="caption" color="error">
                                            Error: Non-Multi-Office license holders cannot have more than one site
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </LocalizationProvider>
                    </FormContainer>
                </Grid>
                {!organisation.archived && <Grid size={{ sm: 12, lg: 6 }} component={Paper}>
                    <Grid container>
                        <Grid>
                            <Typography variant="h6">Organisation Sites</Typography>
                        </Grid>
                        <Grid>
                            {organisation.licenseTypeId == "c86e1a38-70a0-492b-bff7-7e22589a9cdb" &&
                                <Button size="small" onClick={addSite}>Add Site</Button>
                            }
                        </Grid>
                        <Line />
                        <Grid size={12}>
                            <SiteTable organisationId={orgid} refresh={refreshData} siteCount={siteCount} sitesTable={sitesTable} />
                        </Grid>
                    </Grid>
                </Grid>}
                {!organisation.archived && <Grid size={{ sm: 12, lg: 6 }} component={Paper}>
                    <Grid container>
                        <Grid size={12}>
                            <Typography variant="h6">Organisation Users</Typography>
                        </Grid>
                        <Line />
                        <Grid size={12}>
                            <OrgUserManagmentTable orgId={organisation.id} />
                        </Grid>
                    </Grid>
                </Grid>}
                <CreateEditSiteDialog open={dialogOpen} onClose={closeDialog} orgId={orgid} refreshData={refreshData} site={null} />
                <AuditHistoryDialog open={auditOpen} close={() => setAuditOpen(false)} orgId={orgid} />
                <Dialog open={tempExtensionDialogOpen} onClose={() => setTempExtensionDialogOpen(false)} maxWidth="xs" fullWidth>
                    <DialogTitle>Set Temporary Extension</DialogTitle>
                    <DialogContent sx={{ pt: 2 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'en-gb'}>
                            <DatePicker
                                label="Temp Expiry Date"
                                value={tempExtensionDate}
                                onChange={(value) => setTempExtensionDate(value)}
                                format="DD/MM/YYYY"
                                minDate={dayjs(expiryDate) ?? dayjs()}
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            />
                        </LocalizationProvider>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setTempExtensionDialogOpen(false)}>Cancel</Button>
                        <Button onClick={confirmSetTempExtension} variant="contained" disabled={!tempExtensionDate}>Confirm</Button>
                    </DialogActions>
                </Dialog>
                <Dialog open={cancelTempExtensionDialogOpen} onClose={() => setCancelTempExtensionDialogOpen(false)} maxWidth="xs" fullWidth>
                    <DialogTitle>Cancel Temporary Extension</DialogTitle>
                    <DialogContent sx={{ pt: 2 }}>
                        <Typography>Are you sure you want to cancel the temporary extension?</Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setCancelTempExtensionDialogOpen(false)}>No</Button>
                        <Button onClick={async () => {
                            await saveTempExtension(null, () => {
                                setCancelTempExtensionDialogOpen(false);
                            });
                        }} variant="contained">
                            Yes, Cancel Extension
                        </Button>
                    </DialogActions>
                </Dialog>
            </Grid>
            :
            <Grid container justifyContent="center">
                <Grid>
                    <CircularProgress />
                </Grid>
            </Grid>
    );
}
